/**
 * Presentational primitives for the insurance module.
 * Module-local by convention — every module here keeps its own ui.tsx and
 * src/components/ is owned elsewhere.
 *
 * House rules apply: warm tokens from src/styles.css, >=44px touch targets, visible
 * focus, and never status-by-colour-alone (every score carries a mark and a word).
 */
import {
  cloneElement,
  isValidElement,
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
  type ButtonHTMLAttributes,
  type InputHTMLAttributes,
  type ReactElement,
  type ReactNode,
  type SelectHTMLAttributes,
  type TextareaHTMLAttributes,
} from 'react';
import { ApiRequestError, money } from '../../lib/api';
import type { InsuranceRequirement, RequirementScore } from '../../shared/types';

export function cx(...parts: (string | false | null | undefined)[]): string {
  return parts.filter(Boolean).join(' ');
}

export function errorText(err: unknown): string {
  if (err instanceof ApiRequestError) {
    if (err.isForbidden) return 'Insurance is for adults only.';
    // A 404 here means the Worker answered but has no insurance routes — i.e. the API on
    // this host is older than this screen. Raw slugs like "not_found" told the reader
    // nothing; the real answer is almost always "the deploy hasn't landed yet".
    if (err.status === 404) {
      return 'The insurance API is not available on this server yet. If this is the live site, the latest deploy may still be building.';
    }
    return err.message || 'The request failed.';
  }
  return 'Could not reach the household server. Check your connection and try again.';
}

// ---------------------------------------------------------------- data loading

interface Resource<T> {
  data: T | null;
  loading: boolean;
  error: string | null;
  reload: () => void;
  setData: (value: T) => void;
}

/** Load-once-with-reload. Ignores results from a stale request. */
export function useResource<T>(load: () => Promise<T>, deps: unknown[]): Resource<T> {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [nonce, setNonce] = useState(0);
  const loadRef = useRef(load);
  loadRef.current = load;

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    loadRef
      .current()
      .then((result) => {
        if (!cancelled) setData(result);
      })
      .catch((err) => {
        if (!cancelled) setError(errorText(err));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, nonce]);

  const reload = useCallback(() => setNonce((n) => n + 1), []);

  return { data, loading, error, reload, setData };
}

// ---------------------------------------------------------------- surfaces

export type Tone = 'surface' | 'sunk' | 'ok' | 'near' | 'over';

/**
 * One background utility per card, chosen here.
 *
 * Passing `bg-clay-soft` in `className` alongside a baked-in `bg-surface` does NOT
 * work: both are single-class utilities, so the winner is decided by their order in
 * the generated stylesheet, not by the order they appear in the attribute. `bg-surface`
 * happens to win, which silently renders every tinted card white. A `tone` prop is the
 * fix — it emits exactly one background class, so there is nothing to collide.
 */
const CARD_TONES: Record<Tone, string> = {
  surface: 'border-border bg-surface',
  sunk: 'border-border bg-surface-sunk',
  ok: 'border-sage/40 bg-sage-soft',
  near: 'border-honey/40 bg-honey-soft',
  over: 'border-clay/40 bg-clay-soft',
};

export function Card({
  children,
  className,
  tone = 'surface',
  as: Tag = 'div',
}: {
  children: ReactNode;
  className?: string;
  tone?: Tone;
  as?: 'div' | 'section' | 'li';
}) {
  return (
    <Tag className={cx('rounded-card border p-4 sm:p-5', CARD_TONES[tone], className)}>
      {children}
    </Tag>
  );
}

export function PanelHeading({
  title,
  sub,
  action,
}: {
  title: string;
  sub?: string;
  action?: ReactNode;
}) {
  return (
    <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
      <div>
        <h2 className="font-display text-xl font-semibold text-ink">{title}</h2>
        {sub && <p className="mt-1 max-w-prose text-sm text-ink-soft">{sub}</p>}
      </div>
      {action}
    </div>
  );
}

// ---------------------------------------------------------------- controls

type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: 'primary' | 'secondary' | 'ghost' | 'danger';
};

const BUTTON_STYLES: Record<NonNullable<ButtonProps['variant']>, string> = {
  primary: 'bg-sage text-white hover:bg-sage/90 border-transparent',
  secondary: 'bg-surface text-ink border-border hover:bg-surface-sunk',
  ghost: 'bg-transparent text-ink-soft border-transparent hover:bg-surface-sunk hover:text-ink',
  danger: 'bg-transparent text-clay border-border hover:bg-clay-soft',
};

export function Button({ variant = 'secondary', className, ...props }: ButtonProps) {
  return (
    <button
      {...props}
      className={cx(
        'inline-flex min-h-11 items-center justify-center gap-2 rounded-full border px-4 text-sm font-medium',
        'transition-colors focus-visible:ring-2 focus-visible:ring-sage focus-visible:ring-offset-2',
        'focus-visible:ring-offset-canvas focus-visible:outline-none disabled:opacity-50',
        BUTTON_STYLES[variant],
        className
      )}
    />
  );
}

/**
 * A labelled control.
 *
 * The label is a real `htmlFor` label and the hint is wired via `aria-describedby` —
 * NOT nested inside the label. Wrapping the control in a `<label>` folds everything the
 * label contains into the control's accessible name, which for a `<select>` means every
 * option string. The "who we asked" dropdown announced a 393-character name — the word
 * "Who we asked" followed by every contact and their organisation — before its value, on
 * every focus. Name and description are separate things and screen readers treat them so.
 */
export function Field({
  label,
  hint,
  children,
  className,
}: {
  label: string;
  hint?: ReactNode;
  children: ReactNode;
  className?: string;
}) {
  const id = useId();
  const hintId = `${id}-hint`;

  const control = isValidElement(children)
    ? cloneElement(children as ReactElement<{ id?: string; 'aria-describedby'?: string }>, {
        id,
        'aria-describedby': hint ? hintId : undefined,
      })
    : children;

  return (
    <div className={cx('block', className)}>
      <label htmlFor={id} className="mb-1 block text-sm font-medium text-ink">
        {label}
      </label>
      {control}
      {hint && (
        <span id={hintId} className="mt-1 block text-xs text-ink-soft">
          {hint}
        </span>
      )}
    </div>
  );
}

export const inputClass =
  'block min-h-11 w-full rounded-lg border border-border bg-surface px-3 text-base text-ink ' +
  'placeholder:text-ink-faint focus-visible:border-sage focus-visible:ring-2 focus-visible:ring-sage/40 ' +
  'focus-visible:outline-none';

export function TextInput(props: InputHTMLAttributes<HTMLInputElement>) {
  return <input {...props} className={cx(inputClass, props.className)} />;
}

export function Textarea(props: TextareaHTMLAttributes<HTMLTextAreaElement>) {
  return <textarea {...props} className={cx(inputClass, 'min-h-20 py-2', props.className)} />;
}

export function Select(props: SelectHTMLAttributes<HTMLSelectElement>) {
  return <select {...props} className={cx(inputClass, 'pr-8', props.className)} />;
}

// ---------------------------------------------------------------- states

export function Loading({ label, rows = 3 }: { label: string; rows?: number }) {
  return (
    <div role="status" aria-live="polite" className="space-y-3">
      <span className="sr-only">{label}</span>
      {Array.from({ length: rows }).map((_, i) => (
        <div
          key={i}
          className="h-20 animate-pulse rounded-card border border-border bg-surface-sunk"
          aria-hidden="true"
        />
      ))}
    </div>
  );
}

export function ErrorState({ message, onRetry }: { message: string; onRetry: () => void }) {
  return (
    <Card tone="over">
      <p className="text-sm font-medium text-ink">Something went wrong</p>
      <p className="mt-1 text-sm text-ink-soft">{message}</p>
      <Button variant="secondary" className="mt-3" onClick={onRetry}>
        Try again
      </Button>
    </Card>
  );
}

export function EmptyState({
  title,
  children,
  action,
}: {
  title: string;
  children: ReactNode;
  action?: ReactNode;
}) {
  return (
    <Card tone="sunk" className="text-center">
      <h3 className="font-display text-lg font-semibold text-ink">{title}</h3>
      <div className="mx-auto mt-2 max-w-prose text-sm leading-relaxed text-ink-soft">
        {children}
      </div>
      {action && <div className="mt-4 flex flex-wrap justify-center gap-2">{action}</div>}
    </Card>
  );
}

/** Hard rule 6. Coverage comparison reports facts against a checklist — it never advises. */
export function Disclaimer({ children }: { children?: ReactNode }) {
  return (
    <p className="mt-4 rounded-lg border border-border bg-surface-sunk px-3 py-2 text-xs leading-relaxed text-ink-soft">
      <span className="font-medium text-ink">Illustrative, not advice.</span>{' '}
      {children ??
        'This checks each quote against the requirements you wrote down — a binding decision needs a licensed agent.'}
    </p>
  );
}

/** Chip tones are NOT Card tones — a chip has no 'surface'/'sunk', a card has no 'dusk'. */
export type ChipTone = 'neutral' | 'ok' | 'near' | 'over' | 'dusk';

export function Chip({
  children,
  tone = 'neutral',
}: {
  children: ReactNode;
  tone?: ChipTone;
}) {
  // Foreground is text-ink on every tinted variant, and the tint plus border carries the
  // meaning. The accent-on-accent-soft pairings these used to use measured 2.6:1 (honey)
  // to 4.2:1 (clay) at 12px, against a 4.5:1 requirement — and they landed on the highest
  // stakes labels on the screen: the dealbreaker count and the expiry countdown.
  const tones: Record<string, string> = {
    neutral: 'bg-surface-sunk text-ink-soft border-border',
    ok: 'bg-sage-soft text-ink border-sage/40',
    near: 'bg-honey-soft text-ink border-honey/50',
    over: 'bg-clay-soft text-ink border-clay/50',
    dusk: 'bg-dusk-soft text-ink border-dusk/40',
  };
  return (
    <span
      className={cx(
        'inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium whitespace-nowrap',
        tones[tone]
      )}
    >
      {children}
    </span>
  );
}

// ---------------------------------------------------------------- score marks

const SCORE_META: Record<RequirementScore, { mark: string; word: string; className: string }> = {
  met: { mark: '✓', word: 'Met', className: 'text-sage' },
  violated: { mark: '✗', word: 'Not met', className: 'text-clay' },
  // ink-faint measured 2.7:1 on a zebra row. The dash is the mark the copy explicitly calls
  // "a question to take back to the agent" — it cannot be the faintest thing on the page.
  unknown: { mark: '–', word: 'Unknown', className: 'text-ink-soft' },
};

/**
 * A single scored cell. The mark is never the only signal — the word goes in the accessible
 * name, so a screen-reader user and a colour-blind reader both get the answer.
 *
 * `severe` matters for more than emphasis: a dealbreaker and a soft preference miss both
 * render as ✗, and the difference was carried by cell tint alone — invisible to a screen
 * reader, which got identical output for "this disqualifies the quote" and "this is a nice
 * to have". Now the severity is in the name.
 */
export function ScoreMark({
  score,
  detail,
  severe,
}: {
  score: RequirementScore;
  detail?: string;
  severe?: boolean;
}) {
  const meta = SCORE_META[score];
  const word = score === 'violated' && severe ? `${meta.word} — dealbreaker` : meta.word;
  return (
    <span className="inline-flex flex-col items-center gap-0.5">
      <span
        aria-hidden="true"
        className={cx('text-base leading-none font-semibold', meta.className)}
      >
        {meta.mark}
      </span>
      <span className="sr-only">
        {word}
        {detail ? `: ${detail}` : ''}
      </span>
      {detail && (
        <span className="text-[11px] leading-tight text-ink-soft tabular-nums" aria-hidden="true">
          {detail}
        </span>
      )}
    </span>
  );
}

export function scoreWord(score: RequirementScore): string {
  return SCORE_META[score].word;
}

// ---------------------------------------------------------------- formatting

/**
 * A coverage value as a human reads it. Cents only become dollars when the
 * requirement compares them as money — a limit is money, a form code is not.
 */
export function formatActual(
  req: InsuranceRequirement,
  actual: number | string | boolean | null
): string | null {
  if (actual === null) return null;
  if (typeof actual === 'boolean') {
    // An `absent` requirement is phrased as a negative ("No pool liability exclusion"),
    // so a bare "Yes" beside a ✗ reads as if the requirement were satisfied. Name the
    // finding instead of echoing the raw boolean.
    if (req.operator === 'absent') return actual ? 'Excluded' : 'Not excluded';
    return actual ? 'Yes' : 'No';
  }
  if (typeof actual === 'number') {
    return req.operator === 'min' || req.operator === 'max' ? money(actual) : String(actual);
  }
  return actual.toUpperCase().length <= 4 ? actual.toUpperCase() : actual;
}

/** True when this requirement carries a rule the compare table can score automatically. */
export function isScored(req: InsuranceRequirement): boolean {
  return Boolean(req.operator && req.coverage_key);
}

/**
 * What the requirement needed — phrased for the failing cell, not the row header.
 *
 * This used to sit in the row header, where it just restated the label ("…at least
 * $50,000" beside "at least $50,000"). It belongs next to the shortfall instead:
 * "$10,000 · needs $50,000" answers "why did this fail" where the failure is, rather
 * than making the reader track back across the table to find the threshold.
 */
export function requirementNeed(req: InsuranceRequirement): string | null {
  switch (req.operator) {
    case 'min':
      return req.threshold_cents === null ? null : `needs ${money(req.threshold_cents)}+`;
    case 'max':
      return req.threshold_cents === null ? null : `max ${money(req.threshold_cents)}`;
    case 'equals':
      return req.threshold_text ? `needs ${req.threshold_text.toUpperCase()}` : null;
    case 'not_equals':
      return req.threshold_text ? `not ${req.threshold_text.toUpperCase()}` : null;
    // Nothing to add for present/absent: there is no threshold, and the label already
    // states the requirement. "Excluded · must not be excluded" said it twice.
    case 'present':
    case 'absent':
    default:
      return null;
  }
}

/** "expires in 6 days" / "expired 6 days ago" / null when no expiry is recorded. */
export function validityLabel(days: number | null): string | null {
  if (days === null) return null;
  if (days === 0) return 'expires today';
  if (days > 0) return `expires in ${days} day${days === 1 ? '' : 's'}`;
  const ago = Math.abs(days);
  return `expired ${ago} day${ago === 1 ? '' : 's'} ago`;
}

export function validityTone(days: number | null): 'neutral' | 'ok' | 'near' | 'over' {
  if (days === null) return 'neutral';
  if (days < 0) return 'over';
  if (days <= 7) return 'near';
  return 'ok';
}
